// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { getEmissionWasteUnits } from 'apis/operator_site/siteApi';
import { PrimaryTemplate } from 'components/templates/PrimaryTemplate';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BatchRegistrationLocalData,
  BatchRegistrationWasteUnit,
  ItemType,
  Tenant,
} from 'util/Types';
import styles from './BatchRegistration.module.scss';
import { EmissionWasteUnitKind } from 'util/Enums';
import BatchRegistrationFilter from 'components/molecules/BatchRegistrationFilter';
import BatchRegistrationTable from 'components/molecules/BatchRegistrationTable';
import { DbManager } from 'util/DbManager';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { addLoading, endLoading } from 'redux/slices/loadingSlice';
import { handleError } from 'util/errorHandler';
import { createAxios } from 'ts/createAxios';
import { API_ENDPOINTS } from 'util/endPoints';
import { toast } from 'react-toastify';
import { nonNull, normalizeNumber } from 'util/commons';
import BatchRegistrationConfirmModal from 'components/molecules/BatchRegistrationConfirmModal';
import { SelectOption } from 'components/molecules/SelectField';
import { selectTenants } from 'redux/slices/siteInfoSlice';

const BatchRegistration: React.FC = () => {
  const { t } = useTranslation();
  const [wasteUnits, setWasteUnits] = useState<BatchRegistrationWasteUnit[]>(
    []
  );
  const [selectedKind, setSelectedKind] = useState<
    EmissionWasteUnitKind | undefined
  >();
  const [selectedTenantId, setSelectedTenantId] = useState<
    number | undefined
  >();
  const [savedLocalData, setSavedLocalData] = useState<
    BatchRegistrationLocalData | undefined
  >();
  const [localDataUpdated, setLocalDataUpdated] = useState<boolean>(false);
  const [confirmModalOpened, setConfirmModalOpened] = useState<boolean>(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const globalTenants = useSelector(selectTenants);
  const { userId, siteId } = useSelector(selectUser);
  const dispatch = useDispatch();

  const handleUpdateSavedLocalData = async (
    newData: BatchRegistrationWasteUnit[]
  ) => {
    dispatch(addLoading());

    try {
      const localData = await DbManager.batchRegistrationData.get(userId);

      if (localData?.data) {
        const savedIds = Object.keys(localData.data).map(Number);
        const newIds = newData.map(({ id }) => id);

        const idsToRemove = savedIds.filter((id) => !newIds.includes(id));
        idsToRemove.forEach((id) => {
          delete localData.data[id];
        });

        await DbManager.batchRegistrationData.put({
          userId,
          data: localData.data,
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(endLoading());
      setLocalDataUpdated(true);
    }
  };

  useEffect(() => {
    getEmissionWasteUnits({
      successCallback: (data) => {
        setWasteUnits(data);
        handleUpdateSavedLocalData(data);
      },
      orderBy: 'registDatetime:asc',
    });

    if (globalTenants.length > 0) {
      setTenants(globalTenants);
    } else {
      createAxios()
        .get<{ tenants: Tenant[] }>(API_ENDPOINTS.TENANTS, {
          params: {
            siteId,
          },
        })
        .then(({ data: { tenants } }) => {
          setTenants(tenants);
        })
        .catch((error) => {
          handleError(error);
        });
    }
  }, []);

  const tenantOptions: SelectOption[] = useMemo(() => {
    return tenants.map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    }));
  }, [tenants]);

  const displayWasteUnits = useMemo(() => {
    if (!selectedKind && selectedTenantId == null) {
      return wasteUnits;
    }

    return wasteUnits.filter((unit) => {
      const selectedTenant = tenants.find((t) => t.id === selectedTenantId);

      return (
        (!selectedKind || unit.kind === selectedKind) &&
        (selectedTenant == null ||
          selectedTenant.itemItems == null ||
          selectedTenant.itemItems.includes(unit.id))
      );
    });
  }, [wasteUnits, selectedKind, selectedTenantId]);

  const canRegister = useMemo(
    () =>
      Object.values(savedLocalData || {}).length > 0 &&
      !Object.values(savedLocalData || {}).every(
        (values) => !values?.length || values?.every((val) => val == null)
      ),
    [savedLocalData]
  );

  const totalData = useMemo(
    () =>
      displayWasteUnits.map((unit) => {
        const validValues =
          savedLocalData?.[unit.id]?.filter((val) => val != null) || [];

        const sum: number = validValues.reduce(
          (acc: number, val) => acc + (val || 0),
          0
        );

        return {
          id: unit.id,
          totalWeight:
            unit.type === ItemType.FIX && unit.convertKg
              ? sum * Number(unit.convertKg)
              : sum,
          totalCount: validValues.length,
        };
      }),
    [displayWasteUnits, savedLocalData]
  );

  const handleRegistration = async () => {
    if (!canRegister) {
      return;
    }

    if (!confirmModalOpened) {
      setConfirmModalOpened(true);

      return;
    }

    try {
      await createAxios().post(API_ENDPOINTS.EMISSIONS, {
        waste: Object.entries(savedLocalData || {})
          .map(([id, values]) => {
            const filteredValues = values?.filter((val) => val != null) || [];

            if (!filteredValues.length) {
              return null;
            }

            return filteredValues.map((val) => ({
              ...(selectedTenantId != null && { tenantId: selectedTenantId }),
              wasteUnitId: Number(id),
              quantity: val || 0,
            }));
          })
          .flat()
          .filter((item) => item != null),
      });

      toast.success(t('messages.M_109'));
      setSavedLocalData(undefined);
      setConfirmModalOpened(false);

      try {
        dispatch(addLoading());

        const currData = await DbManager.batchRegistrationData.get(userId);

        if (currData?.data) {
          await DbManager.batchRegistrationData.update(userId, {
            data: Object.fromEntries(
              Object.entries(currData.data).filter(
                ([id]) => !Object.keys(savedLocalData || {}).includes(id)
              )
            ),
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        dispatch(endLoading());
      }
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <PrimaryTemplate h1={t('nav.batch_registration')}>
      <div className={styles.BatchRegistration}>
        <div className={styles.Top}>
          <div className={styles.Summary}>
            <p>
              {t('common_history_field.total_selected_weight', {
                totalWeight: normalizeNumber({
                  value: totalData.reduce(
                    (acc, item) => acc + item.totalWeight,
                    0
                  ),
                }),
              })}
            </p>
            <p>
              {t('collect_registration.total_count', {
                count: normalizeNumber({
                  value: totalData.reduce(
                    (acc, item) => acc + item.totalCount,
                    0
                  ),
                }) as never,
              })}
            </p>
          </div>

          <BatchRegistrationFilter
            categoryFilterDispatcher={[selectedKind, setSelectedKind]}
            tenantFilterDispatcher={[selectedTenantId, setSelectedTenantId]}
            tenantFilterOptions={tenantOptions}
            wasteUnits={wasteUnits}
          />
        </div>

        <BatchRegistrationTable
          displayWasteUnits={displayWasteUnits}
          savedLocalData={savedLocalData}
          setSavedLocalData={setSavedLocalData}
          localDataUpdated={localDataUpdated}
          totalData={totalData}
        />

        <div className={styles.Bottom}>
          <button
            className={styles.RegisterButton}
            disabled={!canRegister}
            onClick={handleRegistration}
          >
            {t('emission_input.button_submit')}
          </button>
        </div>
      </div>

      {confirmModalOpened && (
        <BatchRegistrationConfirmModal
          handleRegistration={handleRegistration}
          onClose={() => setConfirmModalOpened(false)}
          showTenantSelectAlert={
            selectedTenantId == null && tenantOptions.length > 0
          }
          confirmContent={totalData
            .filter(({ totalCount }) => totalCount > 0)
            .map(({ id, totalCount, totalWeight }) => ({
              unit: nonNull(displayWasteUnits.find((unit) => unit.id === id)),
              totalCount,
              totalWeight,
            }))}
        />
      )}
    </PrimaryTemplate>
  );
};

export default BatchRegistration;
